/**
 * SourceUtils.ts — Shared source citation templates, generation, and helpers.
 *
 * Extracted from SourceManager.tsx so other components/services can reuse
 * SOURCE_TEMPLATES, generateCitation, and related utilities without pulling
 * in the full React component.
 */

import { Source, SourceType, GenealogyEvent, Person } from '../types.ts';
import { GEDCOM_TAG_TO_EVENT } from './EventUtils.ts';
import { formatDatumCijfers } from '../utils/dateUtils.ts';

// ─── Template field definition ──────────────────────────────────────────────

export interface TemplateField {
  key: string;
  labelKey: string;
  span?: 1 | 2;
  /**
   * PA-voorstel v3 (#1962) — komt uit de akte zelf (archiefimport): alleen-lezen, alleen
   * zichtbaar met een waarde, en telt niet mee in de compleetheid (shared/sourceCompleteness.js).
   */
  alleenMetWaarde?: boolean;
  /** PA-voorstel v5 (#1962) — bewaard als ISO-datum, getoond als dd-mm-jjjj (zoals de meldingen). */
  datum?: boolean;
}

// ─── Evidence Explained — template field definitions per source type ────────

/**
 * Het archiefadres van een stuk: waar het fysiek ligt.
 *
 * Deze drie stonden in geen enkel sjabloon, terwijl de archiefimport ze wel
 * ophaalt. Gevolg: het toegangsnummer dat OpenArchieven meestuurt had nergens
 * een veld om in te landen, en de collectienaam werd wel opgeslagen maar nooit
 * getoond. Zonder toegang + inventarisnummer is een Nederlandse
 * archiefverwijzing niet na te lopen — ze horen bij elke aktesoort die uit een
 * archief komt.
 */
const ARCHIEFADRES_VELDEN: TemplateField[] = [
  { key: 'toegangsnummer', labelKey: 'srcAccessNumber' },
  { key: 'inventarisnummer', labelKey: 'srcInventoryNumber' },
  { key: 'collection', labelKey: 'srcCollectionField', span: 2 },
  // PA-voorstel v3 (#1962) — folio en akte-ID die de archiefimport meestuurt.
  { key: 'folio', labelKey: 'srcArchiveFolio', alleenMetWaarde: true },
  { key: 'akteId', labelKey: 'srcRecordId', alleenMetWaarde: true },
  // PA-voorstel v5 (#1962) — de datum waarop de archiefimport de akte vond (de server zet hem).
  { key: 'geraadpleegdOp', labelKey: 'srcGevondenOp', alleenMetWaarde: true, datum: true },
];

/**
 * Besluit #1962 — één folioveld. De burgerlijke stand heeft al "Folionummer" (`folioNumber`,
 * bewerkbaar); daar krijgt de folio uit de akte geen tweede, alleen-lezen veld naast.
 */
const ARCHIEFADRES_EIGEN_FOLIO = ARCHIEFADRES_VELDEN.filter((f) => f.key !== 'folio');

/** PA-voorstel v5 — de waarde zoals het veld hem toont: een datumveld in dezelfde vorm als de meldingen. */
export function veldWaarde(field: TemplateField, waarde: string | undefined): string {
  return field.datum ? formatDatumCijfers(waarde) : waarde || '';
}

/** PA-voorstel v3 — de velden om te tonen: een alleen-met-waarde-veld valt weg zonder waarde. */
export function zichtbareVelden(
  fields: TemplateField[],
  waarden: Record<string, string> | undefined
): TemplateField[] {
  return fields.filter((f) => !f.alleenMetWaarde || String(waarden?.[f.key] ?? '').trim());
}

export const SOURCE_TEMPLATES: Record<string, { labelKey: string; fields: TemplateField[] }> = {
  CivilBirth: {
    labelKey: 'srcCivilBirth',
    fields: [
      ...ARCHIEFADRES_EIGEN_FOLIO,
      { key: 'municipality', labelKey: 'srcMunicipality' },
      { key: 'register', labelKey: 'srcRegister' },
      { key: 'year', labelKey: 'srcYear' },
      { key: 'folioNumber', labelKey: 'srcFolio' },
      { key: 'recordNumber', labelKey: 'srcRecordNumber' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  CivilMarriage: {
    labelKey: 'srcCivilMarriage',
    fields: [
      ...ARCHIEFADRES_EIGEN_FOLIO,
      { key: 'municipality', labelKey: 'srcMunicipality' },
      { key: 'register', labelKey: 'srcRegister' },
      { key: 'year', labelKey: 'srcYear' },
      { key: 'folioNumber', labelKey: 'srcFolio' },
      { key: 'recordNumber', labelKey: 'srcRecordNumber' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  CivilDeath: {
    labelKey: 'srcCivilDeath',
    fields: [
      ...ARCHIEFADRES_EIGEN_FOLIO,
      { key: 'municipality', labelKey: 'srcMunicipality' },
      { key: 'register', labelKey: 'srcRegister' },
      { key: 'year', labelKey: 'srcYear' },
      { key: 'folioNumber', labelKey: 'srcFolio' },
      { key: 'recordNumber', labelKey: 'srcRecordNumber' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  ChurchBaptism: {
    labelKey: 'srcChurchBaptism',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'parish', labelKey: 'srcParish' },
      { key: 'church', labelKey: 'srcChurch' },
      { key: 'denomination', labelKey: 'srcDenomination' },
      { key: 'date', labelKey: 'srcDate' },
      { key: 'place', labelKey: 'srcPlace' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  ChurchMarriage: {
    labelKey: 'srcChurchMarriage',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'parish', labelKey: 'srcParish' },
      { key: 'church', labelKey: 'srcChurch' },
      { key: 'denomination', labelKey: 'srcDenomination' },
      { key: 'date', labelKey: 'srcDate' },
      { key: 'place', labelKey: 'srcPlace' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  ChurchBurial: {
    labelKey: 'srcChurchBurial',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'parish', labelKey: 'srcParish' },
      { key: 'church', labelKey: 'srcChurch' },
      { key: 'denomination', labelKey: 'srcDenomination' },
      { key: 'date', labelKey: 'srcDate' },
      { key: 'place', labelKey: 'srcPlace' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  Census: {
    labelKey: 'srcCensus',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'municipality', labelKey: 'srcMunicipality' },
      { key: 'year', labelKey: 'srcYear' },
      { key: 'district', labelKey: 'srcDistrict' },
      { key: 'houseNumber', labelKey: 'srcHouseNumber' },
      { key: 'page', labelKey: 'srcPage' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  PopulationRegister: {
    labelKey: 'srcPopulationRegister',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'municipality', labelKey: 'srcMunicipality' },
      { key: 'yearRange', labelKey: 'srcYearRange' },
      { key: 'volumeNumber', labelKey: 'srcVolumeNumber' },
      { key: 'entryNumber', labelKey: 'srcEntryNumber' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  Succession: {
    labelKey: 'srcSuccession',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'office', labelKey: 'srcOffice' },
      { key: 'memorialNumber', labelKey: 'srcMemorialNumber' },
      { key: 'declarationDate', labelKey: 'srcDeclarationDate' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
      { key: 'deathDate', labelKey: 'srcDeathDate' },
      { key: 'deathPlace', labelKey: 'srcDeathPlace' },
    ],
  },
  Notarial: {
    labelKey: 'srcNotarial',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'notary', labelKey: 'srcNotary' },
      { key: 'municipality', labelKey: 'srcMunicipality' },
      { key: 'repertoireNr', labelKey: 'srcRepertoireNr' },
      { key: 'actType', labelKey: 'srcActType' },
      { key: 'date', labelKey: 'srcDate' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  Military: {
    labelKey: 'srcMilitary',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'municipality', labelKey: 'srcMunicipality' },
      { key: 'register', labelKey: 'srcRegister' },
      { key: 'year', labelKey: 'srcYear' },
      { key: 'recordNumber', labelKey: 'srcRecordNumber' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  Newspaper: {
    labelKey: 'srcNewspaper',
    fields: [
      { key: 'newspaperTitle', labelKey: 'srcNewspaperTitle' },
      { key: 'date', labelKey: 'srcDate' },
      { key: 'page', labelKey: 'srcPage' },
      { key: 'column', labelKey: 'srcColumn' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  OnlineDatabase: {
    labelKey: 'srcOnlineDb',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'databaseName', labelKey: 'srcDatabaseName' },
      { key: 'website', labelKey: 'srcWebsite' },
      { key: 'accessDate', labelKey: 'srcAccessDate' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
  Book: {
    labelKey: 'srcBook',
    fields: [
      { key: 'page', labelKey: 'srcPage' },
      { key: 'column', labelKey: 'srcColumn' },
    ],
  },
  // #581 — 'Overige' krijgt dezelfde velden als een notariële akte (keuze Twan),
  // zodat archiefstukken gestructureerd vastgelegd kunnen worden i.p.v. lege template.
  Other: {
    labelKey: 'srcOther',
    fields: [
      ...ARCHIEFADRES_VELDEN,
      { key: 'notary', labelKey: 'srcNotary' },
      { key: 'municipality', labelKey: 'srcMunicipality' },
      { key: 'repertoireNr', labelKey: 'srcRepertoireNr' },
      { key: 'actType', labelKey: 'srcActType' },
      { key: 'date', labelKey: 'srcDate' },
      { key: 'personName', labelKey: 'srcPersonName', span: 2 },
    ],
  },
};

/**
 * Besluit #1962 — de archiefimport bewaart de folio uit de akte als `folio`. Heeft het
 * brontype een eigen folioveld (`folioNumber`), dan hoort hij dáár: één veld, bewerkbaar.
 * Een al ingevuld Folionummer wint. Toegepast in `sourceFromServer`, dus overal gelijk;
 * bij de eerstvolgende opslag staat hij ook in de database onder `folioNumber`.
 */
export function metEigenFolio(
  type: string,
  velden: Record<string, string>
): Record<string, string> {
  if (!velden?.folio || !SOURCE_TEMPLATES[type]?.fields.some((f) => f.key === 'folioNumber'))
    return velden;
  const { folio, ...rest } = velden;
  return { ...rest, folioNumber: rest.folioNumber || folio };
}

// ─── Citation generator ─────────────────────────────────────────────────────

/**
 * Generates a formatted citation string based on source type and template field values.
 *
 * Geeft een LEGE string terug zodra er geen enkel ingevuld citatieveld is.
 *
 * Waarom die guard hier en niet bij de aanroepers: elke tak hieronder eindigt op
 * `return c + '.'`. Zonder ingevulde velden is `c` leeg en kwam er letterlijk `"."`
 * uit (of bij `CivilBirth` de kale romp `"Burgerlijke Stand, Geboorten."`). De
 * autosave in SourceManager sloeg dat op als `citation: autoCitation || source.citation`
 * — en omdat `"."` truthy is, wíste elke autosave de handgeschreven citatie van de
 * genealoog. Dat is bewijsverlies, het ergste wat dit scherm kan doen.
 *
 * De preview-paden hadden de guard (`hasCitationContent`) al; de twee opslag-paden
 * niet. Eén guard aan de bron dekt alle vier de aanroepers.
 */
/**
 * De vindplaats in het archief, achter de instellingsnaam.
 *
 * Nederlandse archiefverwijzingen sluiten af met toegang + inventarisnummer;
 * zonder die twee kan een collega het stuk niet opvragen. Ze werden wel
 * ingelezen maar nergens uitgeschreven. Levert een lege string wanneer er
 * niets is, zodat elke aktesoort dit onvoorwaardelijk kan aanroepen.
 */
function archiefAdres(f: Record<string, string>): string {
  const delen: string[] = [];
  if (f.collection) delen.push(f.collection);
  if (f.toegangsnummer) delen.push(`toegang ${f.toegangsnummer}`);
  if (f.inventarisnummer) delen.push(`inv.nr. ${f.inventarisnummer}`);
  // Besluit #1962 — nooit dubbel: met een eigen folioveld staat de folio al als "Fol.".
  if (f.folio && !f.folioNumber) delen.push(`folio ${f.folio}`);
  if (f.akteId) delen.push(`akte-ID ${f.akteId}`);
  return delen.length > 0 ? `, ${delen.join(', ')}` : '';
}

// #1773 — bewust Nederlands, niet via i18n: de citatie wordt als brontekst opgeslagen
// (`sources.citation`) en geëxporteerd; ze hoort bij de akte, niet bij de taal van de UI.
export function generateCitation(
  type: string,
  fields: Record<string, string>,
  repoName?: string
): string {
  const f = fields || {};
  if (!hasCitationContent(f)) return '';
  const parts: string[] = [];
  switch (type) {
    case 'CivilBirth': {
      if (f.municipality) parts.push(f.municipality);
      parts.push('Burgerlijke Stand');
      parts.push(f.year ? `Geboorten ${f.year}` : 'Geboorten');
      if (f.register) parts.push(`Reg. ${f.register}`);
      if (f.folioNumber) parts.push(`Fol. ${f.folioNumber}`);
      if (f.recordNumber) parts.push(`Akte nr. ${f.recordNumber}`);
      let c = parts.join(', ');
      if (f.personName) c += `. Geboorteakte van ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'CivilMarriage': {
      if (f.municipality) parts.push(f.municipality);
      parts.push('Burgerlijke Stand');
      parts.push(f.year ? `Huwelijken ${f.year}` : 'Huwelijken');
      if (f.register) parts.push(`Reg. ${f.register}`);
      if (f.folioNumber) parts.push(`Fol. ${f.folioNumber}`);
      if (f.recordNumber) parts.push(`Akte nr. ${f.recordNumber}`);
      let c = parts.join(', ');
      if (f.personName) c += `. Huwelijksakte van ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'CivilDeath': {
      if (f.municipality) parts.push(f.municipality);
      parts.push('Burgerlijke Stand');
      parts.push(f.year ? `Overlijden ${f.year}` : 'Overlijden');
      if (f.register) parts.push(`Reg. ${f.register}`);
      if (f.folioNumber) parts.push(`Fol. ${f.folioNumber}`);
      if (f.recordNumber) parts.push(`Akte nr. ${f.recordNumber}`);
      let c = parts.join(', ');
      if (f.personName) c += `. Overlijdensakte van ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'ChurchBaptism': {
      if (f.parish) parts.push(f.parish);
      if (f.church) parts.push(f.church);
      parts.push('Doopregister');
      if (f.date) parts.push(f.date);
      if (f.place) parts.push(f.place);
      let c = parts.join(', ');
      if (f.personName) c += `. Doop van ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'ChurchMarriage': {
      if (f.parish) parts.push(f.parish);
      if (f.church) parts.push(f.church);
      parts.push('Trouwregister');
      if (f.date) parts.push(f.date);
      if (f.place) parts.push(f.place);
      let c = parts.join(', ');
      if (f.personName) c += `. Huwelijk van ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'ChurchBurial': {
      if (f.parish) parts.push(f.parish);
      if (f.church) parts.push(f.church);
      parts.push('Begraafregister');
      if (f.date) parts.push(f.date);
      if (f.place) parts.push(f.place);
      let c = parts.join(', ');
      if (f.personName) c += `. Begrafenis van ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'Census': {
      if (f.municipality) parts.push(f.municipality);
      parts.push(f.year ? `Volkstelling ${f.year}` : 'Volkstelling');
      if (f.district) parts.push(`Wijk ${f.district}`);
      if (f.houseNumber) parts.push(`Huis ${f.houseNumber}`);
      if (f.page) parts.push(`Pag. ${f.page}`);
      let c = parts.join(', ');
      if (f.personName) c += `. Vermelding van ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'PopulationRegister': {
      parts.push(f.municipality ? `Bevolkingsregister ${f.municipality}` : 'Bevolkingsregister');
      if (f.yearRange) parts.push(f.yearRange);
      // Geen prefix voor volumeNumber / entryNumber — gebruiker typt zelf
      // "deel 3, fol. 47" of "gezin 124" in het label-veld.
      if (f.volumeNumber) parts.push(f.volumeNumber);
      if (f.entryNumber) parts.push(f.entryNumber);
      let c = parts.join(', ');
      if (f.personName) c += `. ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'Succession': {
      parts.push('Memorie van Successie');
      if (f.personName) parts.push(f.personName);
      const death: string[] = [];
      if (f.deathDate) death.push(`overleden ${f.deathDate}`);
      if (f.deathPlace) death.push(`te ${f.deathPlace}`);
      if (death.length) parts.push(death.join(' '));
      if (f.declarationDate) parts.push(`aangegeven ${f.declarationDate}`);
      if (f.office) parts.push(f.office);
      // Geen prefix — gebruiker typt zelf "memorienr. 4567" of "4567".
      if (f.memorialNumber) parts.push(f.memorialNumber);
      let c = parts.join(', ');
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'Notarial':
    case 'Other': {
      // #581 — 'Overige' deelt de notariële-akte velden en dus dezelfde citatie-opmaak.
      if (f.notary) parts.push(`Notaris ${f.notary}`);
      if (f.municipality) parts.push(f.municipality);
      if (f.actType) parts.push(f.actType);
      if (f.repertoireNr) parts.push(`Rep. nr. ${f.repertoireNr}`);
      if (f.date) parts.push(f.date);
      let c = parts.join(', ');
      if (f.personName) c += `. Betreffende ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'Military': {
      if (f.municipality) parts.push(f.municipality);
      parts.push('Militieregister');
      if (f.year) parts.push(f.year);
      if (f.register) parts.push(`Reg. ${f.register}`);
      if (f.recordNumber) parts.push(`Nr. ${f.recordNumber}`);
      let c = parts.join(', ');
      if (f.personName) c += `. ${f.personName}`;
      if (repoName) c += `. ${repoName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'Newspaper': {
      if (f.newspaperTitle) parts.push(f.newspaperTitle);
      if (f.date) parts.push(f.date);
      if (f.page) parts.push(`Pag. ${f.page}`);
      if (f.column) parts.push(`Kol. ${f.column}`);
      let c = parts.join(', ');
      if (f.personName) c += `. Vermelding van ${f.personName}`;
      return c + '.';
    }
    case 'OnlineDatabase': {
      if (f.databaseName) parts.push(f.databaseName);
      if (f.website) parts.push(f.website);
      if (f.accessDate) parts.push(`Geraadpleegd ${f.accessDate}`);
      let c = parts.join(', ');
      if (f.personName) c += `. ${f.personName}`;
      c += archiefAdres(f);
      return c + '.';
    }
    case 'Book': {
      if (f.page) parts.push(`Pag. ${f.page}`);
      if (f.column) parts.push(`Kol. ${f.column}`);
      return parts.length > 0 ? parts.join(', ') + '.' : '';
    }
    default:
      return '';
  }
}

// ─── Auto-title generation for akte-sources (#629, #468) ───────────────────

interface SuggestedTitleInput {
  /** Type event voor welke bron wordt aangemaakt: 'birth' | 'death' | 'marriage'. */
  eventType?: string;
  /** Persoon die nu in de editor staat. Velden zijn optional zodat partial formData werkt. */
  person: { id?: string; givenNames?: string; surnamePrefix?: string; surname?: string };
  /** Event-id om de partner bij een huwelijk te kunnen opzoeken. */
  eventId?: string;
  /** Events van de huidige persoon (uit formData). */
  events?: GenealogyEvent[];
  /** Alle personen in het project — voor lookup via personId wanneer participant-personName ontbreekt. */
  people?: Person[];
}

type NameFields = { givenNames?: string; surnamePrefix?: string; surname?: string };

function fullNameOf(p: NameFields): string {
  return [p.givenNames, p.surnamePrefix, p.surname]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(' ');
}

function surnameOf(p: Pick<NameFields, 'surnamePrefix' | 'surname'>): string {
  return [p.surnamePrefix, p.surname]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * Prefix voor individuele (INDI) event-types. Titel = `{prefix} {volledige naam}`.
 *
 * #1773 — bewust Nederlands (data, geen UI): het resultaat wordt de opgeslagen brontitel.
 */
const INDI_TITLE_PREFIX: Record<string, string> = {
  birth: 'Geboorteakte',
  baptism: 'Doopakte',
  christening: 'Doopakte',
  adult_christening: 'Doopakte',
  confirmation: 'Vormselakte',
  first_communion: 'Communieakte',
  bar_mitzvah: 'Bar Mitzvah-registratie',
  bas_mitzvah: 'Bas Mitzvah-registratie',
  blessing: 'Zegeningsakte',
  ordination: 'Wijdingsakte',
  death: 'Overlijdensakte',
  burial: 'Begrafenisakte',
  cremation: 'Crematieakte',
  adoption: 'Adoptieakte',
  naturalization: 'Naturalisatieakte',
  emigration: 'Emigratiedocument',
  immigration: 'Immigratiedocument',
  census: 'Volkstelling',
  probate: 'Nalatenschapsakte',
  will: 'Testament',
  graduation: 'Diploma',
  retirement: 'Pensioenakte',
  occupation: 'Beroepsregistratie',
  education: 'Opleidingsdocument',
  religion: 'Religieuze registratie',
  title: 'Titelakte',
  residence: 'Bevolkingsregister',
  property: 'Eigendomsakte',
  caste: 'Kaste-registratie',
  physical_description: 'Signalement',
  national_id: 'Identiteitsbewijs',
  nationality: 'Nationaliteitsdocument',
  military: 'Militair dossier',
  relocation: 'Verhuisakte',
  milestone: 'Mijlpaaldocument',
  historical: 'Historisch document',
  other: 'Document',
};

/**
 * Prefix voor familie (FAM) event-types. Titel = `{prefix} {selfSurname}-{partnerSurname}`.
 */
const FAM_TITLE_PREFIX: Record<string, string> = {
  marriage: 'Huwelijksakte',
  divorce: 'Echtscheidingsakte',
  divorce_filed: 'Echtscheidingsverzoek',
  annulment: 'Nietigverklaring',
  engagement: 'Verlovingsakte',
  marriage_bann: 'Huwelijksafkondiging',
  marriage_contract: 'Huwelijkscontract',
  marriage_license: 'Huwelijksvergunning',
  marriage_settlement: 'Huwelijksovereenkomst',
  family_other: 'Familiedocument',
};

/**
 * Bepaalt een voorgestelde titel voor een nieuwe bron op basis van het event-type.
 *
 * - **INDI events** (birth, baptism, death, burial, adoption, …): `{prefix} {volledige naam}`.
 * - **FAM events** (marriage, divorce, engagement, …): `{prefix} {zelf-achternaam}-{partner-achternaam}`,
 *   met zelf-filter zodat de huidige persoon niet per ongeluk als partner wordt gepakt.
 *   Valt terug op `{prefix} {zelf-achternaam}` als er geen partner kan worden bepaald.
 *
 * Pure function — geschikt voor unit tests. Introduceert geen UI-dependencies.
 */
export function generateSuggestedSourceTitle(input: SuggestedTitleInput): string {
  const { eventType, person, eventId, events, people } = input;
  if (!eventType) return '';

  const fullName = fullNameOf(person);
  if (!fullName) return '';

  const famPrefix = FAM_TITLE_PREFIX[eventType];
  if (famPrefix) {
    const selfSurname = surnameOf(person);
    if (!selfSurname) return '';

    const ev = eventId ? events?.find((e) => e.id === eventId) : null;
    // Zoek een partner-participant die NIET de huidige persoon zelf is.
    const partnerParticipant = ev?.participants?.find(
      (p) => p.role === 'partner' && p.personId !== person.id
    );

    // Bepaal partner-achternaam: eerst via lookup in people[] (betrouwbaarst),
    // anders via de personName-string op het participant record.
    let partnerSurname = '';
    if (partnerParticipant?.personId && people) {
      const partnerPerson = people.find((pp) => pp.id === partnerParticipant.personId);
      if (partnerPerson) partnerSurname = surnameOf(partnerPerson);
    }
    if (!partnerSurname && partnerParticipant?.personName) {
      const name = partnerParticipant.personName.trim();
      const parts = name.split(/\s+/);
      partnerSurname = parts.length > 1 ? parts.slice(1).join(' ') : name;
    }

    if (selfSurname && partnerSurname) {
      return `${famPrefix} ${selfSurname}-${partnerSurname}`;
    }
    return `${famPrefix} ${selfSurname}`;
  }

  const indiPrefix = INDI_TITLE_PREFIX[eventType];
  if (indiPrefix) return `${indiPrefix} ${fullName}`;

  return '';
}

// ─── Event-to-SourceType mapping ────────────────────────────────────────────

export const EVENT_TO_SOURCE_TYPE: Record<string, SourceType> = {
  // INDI — civil
  birth: 'CivilBirth',
  death: 'CivilDeath',
  adoption: 'Civil',
  naturalization: 'Civil',
  emigration: 'Civil',
  immigration: 'Civil',
  // INDI — church
  baptism: 'ChurchBaptism',
  christening: 'ChurchBaptism',
  adult_christening: 'ChurchBaptism',
  confirmation: 'Church',
  first_communion: 'Church',
  bar_mitzvah: 'Church',
  bas_mitzvah: 'Church',
  blessing: 'Church',
  ordination: 'Church',
  burial: 'ChurchBurial',
  cremation: 'ChurchBurial',
  // INDI — notarial / administratief
  probate: 'Notarial',
  will: 'Notarial',
  property: 'Notarial',
  // INDI — overig
  census: 'Census',
  military: 'Military',
  occupation: 'Other',
  education: 'Other',
  graduation: 'Other',
  retirement: 'Other',
  religion: 'Other',
  title: 'Other',
  residence: 'PopulationRegister',
  caste: 'Other',
  physical_description: 'Other',
  national_id: 'Other',
  nationality: 'Other',
  relocation: 'Other',
  milestone: 'Other',
  historical: 'Other',
  other: 'Other',
  // FAM
  marriage: 'CivilMarriage',
  divorce: 'Civil',
  divorce_filed: 'Civil',
  annulment: 'Civil',
  engagement: 'Civil',
  // #1389 — de ondertrouw staat in het trouwboek; 'Church' (legacy) staat niet eens in de dropdown.
  marriage_bann: 'ChurchMarriage',
  marriage_contract: 'Notarial',
  marriage_license: 'Civil',
  marriage_settlement: 'Notarial',
  family_other: 'Other',
};

// ─── Bron-type opties voor UI-dropdowns ─────────────────────────────────────
// Gedeeld tussen CreateSourceModal en het GEDCOM-import-reviewpaneel (#zie
// 2026-08-04-gedcom-source-type-review-design.md) zodat er niet twee losse
// lijsten uit elkaar kunnen lopen.
export const SOURCE_TYPE_OPTIONS: SourceType[] = [
  'CivilBirth',
  'CivilMarriage',
  'CivilDeath',
  'ChurchBaptism',
  'ChurchMarriage',
  'ChurchBurial',
  'Census',
  'PopulationRegister',
  'Succession',
  'Notarial',
  'Military',
  'Newspaper',
  'OnlineDatabase',
  'Book',
  'Other',
];

// Legacy SourceType-waarden die EVENT_TO_SOURCE_TYPE hierboven nog steeds produceert voor
// standaard GEDCOM-tags (adoption/naturalization/… → 'Civil', confirmation/blessing/… →
// 'Church'), maar die SOURCE_TYPE_OPTIONS bewust uitsluit uit de curated dropdown-lijst.
// SOURCE_TEMPLATES heeft geen entry voor deze twee (geen citatie-velden), dus geen nieuwe
// i18n-keys — hergebruik de bestaande sourceCategory*-labels die al precies dit concept
// dekken. Gedeeld tussen ImportReviewPanel.tsx (eigen kopie, ongewijzigd gelaten) en
// SourceTypeReview.tsx zodat de laatste geen losse her-implementatie nodig heeft.
export const LEGACY_SOURCE_TYPE_LABEL_KEYS: Partial<Record<SourceType, string>> = {
  Civil: 'sourceCategoryCivil',
  Church: 'sourceCategoryChurch',
};

// ─── GEDCOM helpers for inline source naming ────────────────────────────────

/**
 * Maps a GEDCOM event tag (BIRT, DEAT, MARR, etc.) to a semantic event type.
 *
 * Alias van `GEDCOM_TAG_TO_EVENT` uit EventUtils — bewust als string-record
 * geëxporteerd (niet typed union) omdat SourceUtils-consumers strings
 * doorgeven naar i18n-lookups. Volledige mapping (30+ tags) i.p.v. de legacy
 * 9-tag subset om drift met EventUtils te voorkomen.
 *
 * #701 — voorheen had deze map `CHR → 'baptism'` terwijl EventUtils
 * `CHR → 'christening'` had. Consolidatie naar canonical EventUtils-versie;
 * `getEventSourceLabel()` hieronder vertaalt `christening` naar de juiste
 * i18n-key zodat de bron-naam ("Doopakte") blijft werken.
 */
export const GEDCOM_TAG_TO_EVENT_TYPE: Record<string, string> = GEDCOM_TAG_TO_EVENT;

/** Returns a human-readable source label for an event type (via i18n), optionally with person name(s) */
export function getEventSourceLabel(
  eventType: string,
  t: (key: string) => string,
  personNames?: string[]
): string {
  const map: Record<string, string> = {
    birth: 'gedcomSourceBirth',
    death: 'gedcomSourceDeath',
    marriage: 'gedcomSourceMarriage',
    marriage_bann: 'gedcomSourceMarriage', // #1389 — de ondertrouw staat in dezelfde akte
    baptism: 'gedcomSourceBaptism',
    christening: 'gedcomSourceBaptism', // #701 — CHR valt onder Doopakte (zelfde bron-type als BAPM)
    burial: 'gedcomSourceBurial',
    divorce: 'gedcomSourceDivorce',
    emigration: 'gedcomSourceEmigration',
    census: 'gedcomSourceCensus',
    military: 'gedcomSourceMilitary',
  };
  const base = map[eventType] ? t(map[eventType]) : t('gedcomSourceGeneric');
  if (personNames && personNames.length > 0) {
    // #1773 — de bronsoort komt al uit i18n, de verbindingswoorden nu ook ("Birth Document
    // of A and B" i.p.v. "… van A en B"). Zonder vertaler (parseGedcom zonder `t` geeft de
    // sleutel terug) blijft het Nederlands, zoals voorheen.
    const vertaal = (key: string, nl: string) => {
      const s = t(key);
      return s && s !== key ? s : nl;
    };
    const names = personNames.filter((n) => n.trim()).join(` ${vertaal('rcAnd', 'en')} `);
    if (names) {
      return vertaal('srSourceLabelOf', '{base} van {names}')
        .split('{base}')
        .join(base)
        .split('{names}')
        .join(names);
    }
  }
  return base;
}

/** Extracts a readable label from a URL domain (known archives get friendly names) */
export function extractDomainLabel(url: string): string | null {
  try {
    const hostname = new URL(url).hostname.replace(/^(www\.|proxy\.)/, '');
    // #1773 — eigennamen van archieven: niet vertalen.
    const known: Record<string, string> = {
      'allegroningers.nl': 'Alle Groningers',
      'allefriezen.nl': 'Alle Friezen',
      'alledrenten.nl': 'Alle Drenten',
      'allelimburgers.nl': 'Alle Limburgers',
      'archieven.nl': 'Archieven.nl',
      'wiewaswie.nl': 'WieWasWie',
      'openarch.nl': 'Open Archieven',
      'familysearch.org': 'FamilySearch',
      'ancestry.com': 'Ancestry',
      'geneanet.org': 'Geneanet',
      'geni.com': 'Geni',
      'myheritage.com': 'MyHeritage',
      'delpher.nl': 'Delpher',
      'nationaalarchief.nl': 'Nationaal Archief',
      'geldersarchief.nl': 'Gelders Archief',
      'bhic.nl': 'BHIC',
      'hetutrechtsarchief.nl': 'Het Utrechts Archief',
      'stadsarchief.amsterdam': 'Stadsarchief Amsterdam',
      'regionaalarchief.nl': 'Regionaal Archief',
      'regionaalarchieftilburg.nl': 'Regionaal Archief Tilburg',
      'agatha.arch.be': 'Rijksarchief België',
      'search.arch.be': 'Rijksarchief België',
      'drentsarchief.nl': 'Drents Archief',
      'erfgoedcentrumzutphen.nl': 'Erfgoedcentrum Zutphen',
      'historischcentrumoverijssel.nl': 'Historisch Centrum Overijssel',
      'tresoar.nl': 'Tresoar',
      'rug.nl': 'RUG Archief',
      'zoekakten.nl': 'Zoek Akten',
    };
    if (known[hostname]) return known[hostname];
    // Try without first subdomain
    const parts = hostname.split('.');
    if (parts.length > 2) {
      const main = parts.slice(-2).join('.');
      if (known[main]) return known[main];
    }
    // Fallback: capitalize domain
    const fallback = parts.length > 2 ? parts.slice(-2).join('.') : hostname;
    return fallback.charAt(0).toUpperCase() + fallback.slice(1);
  } catch {
    return null;
  }
}

/** Extracts a normalized domain key for repository grouping */
export function extractDomainKey(url: string): string {
  try {
    return new URL(url).hostname.replace(/^(www\.|proxy\.)/, '').toLowerCase();
  } catch {
    return 'unknown';
  }
}

// ─── Source categories ──────────────────────────────────────────────────────

export const SOURCE_CATEGORIES = {
  civil: ['CivilBirth', 'CivilMarriage', 'CivilDeath', 'Civil'] as const,
  church: ['ChurchBaptism', 'ChurchMarriage', 'ChurchBurial', 'Church'] as const,
  other: [
    'Census',
    'Notarial',
    'Military',
    'Newspaper',
    'OnlineDatabase',
    'Book',
    'Other',
  ] as const,
} as const;

export type SourceCategory = 'all' | 'civil' | 'church' | 'other';

// ─── Completeness helper ────────────────────────────────────────────────────

/**
 * Single source of truth for "does this citation_fields object actually carry
 * content?" — i.e. at least one value with non-whitespace text.
 *
 * Findings 46/47: the completeness chip and the backend score used to answer
 * this via mere key-existence (`Object.keys(...).length === 0` /
 * `citation_fields::text != '{}'`), so an object with keys but only empty
 * strings (`{municipality:'', year:''}`) wrongly counted as filled. The citation
 * preview already used trimmed-content semantics; this helper makes the chip
 * match it. The backend score mirrors the same logic in SQL via
 * `EXISTS (... jsonb_each_text WHERE btrim(value) <> '')`.
 */
export function hasCitationContent(fields?: Record<string, unknown> | null): boolean {
  if (!fields) return false;
  // C1 (#1390-review) — nieuwe writers zetten ook een boolean (`titleGuessed`)
  // of een object (`verification: {…}`) in citation_fields; `.trim()` op zo'n
  // waarde crashte de hele pagina (root error boundary via SourceManager).
  return Object.values(fields).some((v) => typeof v === 'string' && v.trim() !== '');
}

export function getSourceCompleteness(source: Source): 'complete' | 'partial' | 'incomplete' {
  const score = source.completenessScore ?? 0;
  // #569 — uniforme drempels (label én kleur gelijk): compleet >=75, partieel >=40.
  if (score >= 75) return 'complete';
  if (score >= 40) return 'partial';
  return 'incomplete';
}

// ─── Soft duplicate detection (#53) ──────────────────────────────────────────

/** Normalizes a source title for loose comparison (trim, lowercase, collapse whitespace). */
function normalizeSourceTitle(title?: string): string {
  return (title || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Lightweight, conservative soft-duplicate finder (#53). Returns existing sources
 * whose normalized title equals the candidate's normalized title, excluding the
 * candidate's own id. Intentionally simple (exact match after normalization, no
 * fuzzy matching) — this powers a non-blocking hint only, not the full dedup/merge
 * flow (that work is parked). An empty candidate title yields no matches.
 */
export function findSimilarSources(
  candidate: { title?: string; id?: string },
  existing: Source[]
): Source[] {
  const normalized = normalizeSourceTitle(candidate.title);
  if (!normalized) return [];
  return existing.filter(
    (s) => s.id !== candidate.id && normalizeSourceTitle(s.title) === normalized
  );
}
